import React from "react";
import { useAppSelector } from "../../../store/hooks";
import { formatCurrency, formatNumber } from "../../../utils/formatting";
import { BreakawaySection } from "../../../types/breakawayHull";

const ARMOR_TYPE_LABELS: Record<string, string> = {
  titaniumSteel: "Titanium Steel Armor",
  crystaliron: "Crystaliron Armor",
  bondedSuperdense: "Bonded Superdense Armor",
  molecularBonded: "Molecular Bonded Armor",
};

interface BreakawaySectionSummaryProps {
  section: BreakawaySection;
  totalShipSize: number;
}

export const BreakawaySectionSummary: React.FC<BreakawaySectionSummaryProps> = ({
  section,
  totalShipSize,
}) => {
  // Get section-specific data from Redux state
  const sectionData = useAppSelector((state) => {
    const breakawayHull = state.breakawayHull;
    return breakawayHull?.sections?.find(s => s.id === section.id);
  });

  // Get main ship's armor data (breakaway sections inherit from main ship)
  const hull = useAppSelector((state) => state.shipBuilder.hull);
  const mainShipArmor = hull?.armor ?? null;

  // Get weapons assigned to this section from weaponMounts
  const sectionWeapons = useAppSelector((state) => {
    return state.shipBuilder.weaponMounts?.filter(mount => (mount as any).sectionId === section.id) || [];
  });

  // Get optional systems assigned to this section
  const sectionOptionalSystems = useAppSelector((state) => {
    const allSystems = [
      ...(Array.isArray(state.optionalSystems.structure) ? state.optionalSystems.structure : []),
      ...(Array.isArray(state.optionalSystems.modularHull) ? state.optionalSystems.modularHull : []),
      ...(Array.isArray(state.optionalSystems.power) ? state.optionalSystems.power : []),
      ...(Array.isArray(state.optionalSystems.drives) ? state.optionalSystems.drives : []),
      ...(Array.isArray(state.optionalSystems.fuel) ? state.optionalSystems.fuel : []),
      ...(Array.isArray(state.optionalSystems.accommodations) ? state.optionalSystems.accommodations : []),
      ...(Array.isArray(state.optionalSystems.bridge) ? state.optionalSystems.bridge : []),
      ...(Array.isArray(state.optionalSystems.cargo) ? state.optionalSystems.cargo : []),
      ...(Array.isArray(state.optionalSystems.drones) ? state.optionalSystems.drones : []),
      ...(Array.isArray(state.optionalSystems.sensors) ? state.optionalSystems.sensors : []),
      ...(Array.isArray(state.optionalSystems.external) ? state.optionalSystems.external : []),
      ...(Array.isArray(state.optionalSystems.internal) ? state.optionalSystems.internal : []),
      ...(Array.isArray(state.optionalSystems.commonAreas) ? state.optionalSystems.commonAreas : []),
    ];
    return allSystems.filter(system => (system as any).sectionId === section.id);
  });

  // Use sectionData if available, otherwise fall back to section prop
  const currentSection = sectionData || section;

  // Calculate section totals and statistics
  const calculateSectionStats = () => {
    let totalCost = 0;
    let totalTons = 0;
    let totalPower = 0;
    let hullPoints = Math.floor(currentSection.size * 2.5); // Basic hull points calculation

    // Bridge cost and weight
    if (currentSection?.bridge) {
      totalCost += (currentSection.bridge as any).cost || 0;
      totalTons += (currentSection.bridge as any).weight || 0;
      totalPower += (currentSection.bridge as any).power || 0;
    }

    // Power plant cost and weight
    if (currentSection?.powerPlant) {
      totalCost += (currentSection.powerPlant as any).cost || 0;
      totalTons += (currentSection.powerPlant as any).weight || 0;
      totalPower += (currentSection.powerPlant as any).power || 0;
    }

    // Drives cost and weight
    if (currentSection?.drives) {
      Object.values(currentSection.drives).forEach((drive: any) => {
        if (drive) {
          totalCost += drive.cost || 0;
          totalTons += drive.weight || 0;
          totalPower += drive.power || 0;
        }
      });
    }

    // Sensors cost and weight
    if (currentSection?.sensors) {
      totalCost += (currentSection.sensors as any).cost || 0;
      totalTons += (currentSection.sensors as any).weight || 0;
      totalPower += (currentSection.sensors as any).power || 0;
    }

    // Armor cost and weight (use main ship armor for breakaway sections)
    // Note: ArmorConfiguration doesn't have cost/weight/power - these are calculated elsewhere
    // if (mainShipArmor) {
    //   totalCost += mainShipArmor.cost || 0;
    //   totalTons += mainShipArmor.weight || 0;
    //   totalPower += mainShipArmor.power || 0;
    // }

    // Weapons cost and weight
    sectionWeapons.forEach((mount: any) => {
      totalCost += mount.cost || 0;
      totalTons += mount.tons || 0;
      totalPower += mount.power || 0;
    });

    // Optional systems cost and weight
    sectionOptionalSystems.forEach((system: any) => {
      totalCost += system.cost || 0;
      totalTons += system.tonnage || 0;
      totalPower += system.power || 0;
    });

    // Stateroom cost and weight
    if (currentSection?.stateroomConfig) {
      const config = currentSection.stateroomConfig;
      // Single staterooms: 4 tons each, 1 power each, 1M Cr each
      totalTons += config.singleStaterooms * 4;
      totalPower += config.singleStaterooms * 1;
      totalCost += config.singleStaterooms * 1000000;
      
      // Double staterooms: 6 tons each, 1 power each, 1.5M Cr each
      totalTons += config.doubleStaterooms * 6;
      totalPower += config.doubleStaterooms * 1;
      totalCost += config.doubleStaterooms * 1500000;
      
      // Low berths: 0.5 tons each, 0 power, 0.1M Cr each
      totalTons += config.lowBerths * 0.5;
      totalCost += config.lowBerths * 100000;
      
      // Emergency low berths: 1 ton each, 0 power, 0.1M Cr each
      totalTons += config.emergencyLowBerths * 1;
      totalCost += config.emergencyLowBerths * 100000;
      
      // Common area: 0.1 tons per ton, 0 power, 0.1M Cr per ton
      totalTons += config.commonArea * 0.1;
      totalCost += config.commonArea * 100000;
    }

    return { totalCost, totalTons, totalPower, hullPoints };
  };

  const stats = calculateSectionStats();
  const sectionPercentage = ((section.size / totalShipSize) * 100).toFixed(1);

  // Group drives by type and rating
  const groupedDrives = () => {
    if (!currentSection?.drives) return [];
    
    const driveGroups: { [key: string]: { type: string; rating: number; count: number } } = {};
    
    Object.values(currentSection.drives).forEach((drive: any) => {
      if (drive) {
        const key = `${drive.type}-${drive.rating}`;
        if (driveGroups[key]) {
          driveGroups[key].count++;
        } else {
          driveGroups[key] = {
            type: drive.type,
            rating: drive.rating,
            count: 1
          };
        }
      }
    });

    return Object.values(driveGroups);
  };


  // Calculate power requirements
  const calculatePowerRequirements = () => {
    let basicSystemsPower = 0;
    let jumpDrivePower = 0;
    let weaponsPower = 0;
    let maneuverDrivePower = 0;
    let sensorsPower = 0;
    let bridgePower = 0;
    let optionalSystemsPower = 0;
    let stateroomsPower = 0;

    // Basic systems (hull, life support, etc.) - 20% of section size
    basicSystemsPower = Math.floor(currentSection.size * 0.2);

    // Drives - use same formulas as main ship
    if (currentSection?.drives) {
      Object.values(currentSection.drives).forEach((drive: any) => {
        if (drive) {
          if (drive.type === 'jump') {
            // Jump drive: 10% of section size × jump rating
            jumpDrivePower += Math.ceil(currentSection.size * 0.1 * drive.rating);
          } else if (drive.type === 'maneuver') {
            // Maneuver drive: 10% of section size × thrust rating
            const thrustMultiplier = drive.rating === 0 ? 0.25 : 1;
            maneuverDrivePower += Math.ceil(currentSection.size * 0.1 * drive.rating * thrustMultiplier);
          }
        }
      });
    }

    // Weapons
    sectionWeapons.forEach((mount: any) => {
      weaponsPower += mount.power || 0;
    });

    // Optional systems
    sectionOptionalSystems.forEach((system: any) => {
      optionalSystemsPower += system.power || 0;
    });

    // Sensors
    if (currentSection?.sensors) {
      sensorsPower = (currentSection.sensors as any).power || 0;
    }

    // Bridge
    if (currentSection?.bridge) {
      bridgePower = (currentSection.bridge as any).power || 0;
    }

    // Staterooms power - use same formula as main ship
    if (currentSection?.stateroomConfig) {
      const config = currentSection.stateroomConfig;
      // Standard staterooms: 0 power
      stateroomsPower += (config.singleStaterooms || 0) * 0;
      stateroomsPower += (config.doubleStaterooms || 0) * 0;
      // Low berths: 1 power per 10 berths
      stateroomsPower += Math.ceil((config.lowBerths || 0) / 10);
      // Emergency low berths: 1 power each
      stateroomsPower += (config.emergencyLowBerths || 0) * 1;
      // Common area: 0.5 power per ton
      stateroomsPower += (config.commonArea || 0) * 0.5;
    }

    return {
      basicSystemsPower,
      jumpDrivePower,
      weaponsPower,
      maneuverDrivePower,
      sensorsPower,
      bridgePower,
      optionalSystemsPower,
      stateroomsPower,
      total: basicSystemsPower + jumpDrivePower + weaponsPower + maneuverDrivePower + sensorsPower + bridgePower + optionalSystemsPower + stateroomsPower
    };
  };

  const powerRequirements = calculatePowerRequirements();


  return (
    <div className="bg-white dark:bg-gray-800 p-6 md:p-10 rounded-lg shadow w-full border border-gray-300 dark:border-gray-700 print:border-0 print:shadow-none mb-8">
      {/* Top Row: Section Name, Hull Points, Armour */}
      <div className="flex flex-wrap gap-4 mb-4 items-start">
        {/* Section Name and Size (stacked) */}
        <div className="flex-1 min-w-[300px] flex flex-col gap-2">
          <div>
            <div className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              SECTION NAME
            </div>
            <div className="flex items-center w-full border border-t-0 border-blue-200 dark:border-blue-700 rounded-b-md bg-white dark:bg-gray-700">
              <div className="px-3 py-2 h-10 flex items-center text-base text-gray-900 dark:text-gray-100 flex-shrink-0 min-w-[220px]">
                {currentSection.name}
              </div>
              <div className="flex-1 flex justify-end overflow-hidden">
                <span className="px-3 py-2 h-10 flex items-center text-base text-gray-900 dark:text-gray-100 whitespace-nowrap text-right bg-white dark:bg-gray-700 mr-2 truncate">
                  {formatNumber(currentSection.size)} tons • {sectionPercentage}% of total
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Hull Points and Armour (vertical stack) */}
        <div className="flex flex-col gap-2 min-w-[120px]">
          <div>
            <div className="bg-orange-400 text-white font-bold px-3 py-1 rounded-t-md text-xs tracking-wide text-center">
              HULL POINTS
            </div>
            <div className="border border-t-0 border-orange-400 rounded-b-md px-3 py-2 h-10 text-center text-lg font-semibold text-gray-900 dark:text-gray-100">
              {stats.hullPoints}
            </div>
          </div>
          <div>
            <div className="bg-orange-400 text-white font-bold px-3 py-1 rounded-t-md text-xs tracking-wide text-center">
              ARMOUR
            </div>
            <div className="border border-t-0 border-orange-400 rounded-b-md px-3 py-2 h-14 text-center flex flex-col items-center justify-center">
              <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                {mainShipArmor?.type 
                  ? ARMOR_TYPE_LABELS[mainShipArmor.type] || mainShipArmor.type
                  : 'Standard'}
              </span>
              <span className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {mainShipArmor?.rating || 0}
              </span>
            </div>
          </div>
        </div>
      </div>


      {/* Main Content Grid - 3 columns like main Ship Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="flex flex-col gap-4">
          {/* Section Statistics - Orange highlight boxes */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="bg-orange-400 text-white font-bold px-2 py-1 rounded-t-md text-xs text-center">
                SECTION COST
              </div>
              <div className="border border-t-0 border-orange-400 rounded-b-md px-2 py-1 text-xs text-center text-gray-900 dark:text-gray-100">
                {formatCurrency(stats.totalCost)}
              </div>
            </div>
            <div>
              <div className="bg-orange-400 text-white font-bold px-2 py-1 rounded-t-md text-xs text-center">
                WEIGHT
              </div>
              <div className="border border-t-0 border-orange-400 rounded-b-md px-2 py-1 text-xs text-center text-gray-900 dark:text-gray-100">
                {formatNumber(stats.totalTons)} tons
              </div>
            </div>
            <div>
              <div className="bg-orange-400 text-white font-bold px-2 py-1 rounded-t-md text-xs text-center">
                POWER OUTPUT
              </div>
              <div className="border border-t-0 border-orange-400 rounded-b-md px-2 py-1 text-xs text-center text-gray-900 dark:text-gray-100">
                {formatNumber(stats.totalPower)}
              </div>
            </div>
            <div>
              <div className="bg-orange-400 text-white font-bold px-2 py-1 rounded-t-md text-xs text-center">
                SECTION SIZE
              </div>
              <div className="border border-t-0 border-orange-400 rounded-b-md px-2 py-1 text-xs text-center text-gray-900 dark:text-gray-100">
                {formatNumber(section.size)} tons
              </div>
            </div>
          </div>

          {/* Ship's Computer Section */}
          <div>
            <div className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              SHIP'S COMPUTER
            </div>
            <div className="border border-t-0 border-blue-200 dark:border-blue-700 rounded-b-md p-3">
              <div className="text-xs text-gray-700 dark:text-gray-300 space-y-1 mb-3">
                {(currentSection as any)?.computer ? (
                  <div className="space-y-1">
                    <div className="flex justify-between items-center border-b border-dashed border-gray-300 dark:border-gray-600 py-1">
                      <span>Primary: {(currentSection as any).computer.model || 'Computer/1'}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {(currentSection as any).computer.processing} processing
                      </span>
                    </div>
                    {(currentSection as any).computer.options && (currentSection as any).computer.options.length > 0 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        Options: {(currentSection as any).computer.options.join(', ')}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-gray-500 dark:text-gray-400 italic">No computer assigned</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Center Column */}
        <div className="flex flex-col gap-4">
          {/* Power Requirement */}
          <div>
            <div className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide text-center">
              POWER REQUIREMENT
            </div>
            <div className="border border-t-0 border-gray-200 dark:border-gray-600 rounded-b-md p-3 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-xs text-gray-900 dark:text-gray-100">POWER POINTS</span>
                <span className="border-b border-gray-400 dark:border-gray-500 flex-1 px-2 py-1 text-right font-semibold text-gray-900 dark:text-gray-100">
                  {(currentSection?.powerPlant as any)?.power || 0}
                </span>
              </div>
              <div className="flex flex-col gap-1 mt-2 text-xs text-gray-900 dark:text-gray-100">
                <div>
                  BASIC SHIP SYSTEMS ▶{" "}
                  {powerRequirements.basicSystemsPower > 0 ? powerRequirements.basicSystemsPower : "—"}
                </div>
                <div>
                  JUMP DRIVE ▶ {powerRequirements.jumpDrivePower > 0 ? powerRequirements.jumpDrivePower : "—"}
                </div>
                <div>
                  WEAPONS ▶{" "}
                  {powerRequirements.weaponsPower > 0 ? powerRequirements.weaponsPower : "—"}
                </div>
                <div>
                  MANOEUVRE DRIVE ▶{" "}
                  {powerRequirements.maneuverDrivePower > 0 ? powerRequirements.maneuverDrivePower : "—"}
                </div>
                <div>SENSORS ▶ {powerRequirements.sensorsPower > 0 ? powerRequirements.sensorsPower : "—"}</div>
                <div>BRIDGE ▶ {powerRequirements.bridgePower > 0 ? powerRequirements.bridgePower : "—"}</div>
                <div>
                  OPTIONAL SYSTEMS ▶{" "}
                  {powerRequirements.optionalSystemsPower > 0 ? powerRequirements.optionalSystemsPower : "—"}
                </div>
                <div>
                  STATEROOMS ▶ {powerRequirements.stateroomsPower > 0 ? powerRequirements.stateroomsPower : "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Drives */}
          <div>
            <div className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              DRIVES
            </div>
            <div className="border border-t-0 border-blue-200 dark:border-blue-700 rounded-b-md p-3 text-xs">
              <div className="flex flex-col gap-2">
                <div>
                  <div className="font-semibold text-blue-800 dark:text-blue-200 mb-1">Full Drive Potential:</div>
                  <div className="flex flex-col gap-1 ml-2 text-gray-900 dark:text-gray-100">
                    {groupedDrives().map((drive, index) => (
                      <div key={index}>
                        {drive.type.toUpperCase()} DRIVE ▶ {drive.type === 'jump' ? 'JUMP' : 'THRUST'}:{" "}
                        <span className="text-center">
                          {drive.rating > 0 ? drive.rating : "—"}
                        </span>
                        {drive.count > 1 && (
                          <span className="text-gray-500 dark:text-gray-400 ml-1">({drive.count}x)</span>
                        )}
                      </div>
                    ))}
                    {groupedDrives().length === 0 && (
                      <div className="text-gray-500 dark:text-gray-400 italic">No drives assigned</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="flex flex-col gap-4">
          {/* Bridge Configuration Box */}
          <div>
            <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              BRIDGE CONFIGURATION
            </div>
            <div className="border border-t-0 border-blue-100 dark:border-blue-700 rounded-b-md p-3 text-xs">
              <div className="flex flex-col gap-1 text-gray-900 dark:text-gray-100">
                <div>
                  <span className="font-semibold">Type:</span>{" "}
                  {(currentSection as any)?.bridge?.type || "—"}
                </div>
                {(currentSection as any)?.bridge?.special && (
                  <div>
                    <span className="font-semibold">Special:</span>{" "}
                    {(currentSection as any).bridge.special}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sensors Table */}
          <div className="sensors-section">
            <div className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              SENSORS
            </div>
            <table className="w-full border border-blue-200 dark:border-blue-700 rounded-b-md text-xs">
              <thead>
                <tr className="bg-blue-50 dark:bg-blue-900/30">
                  <th className="p-1 border border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100">TYPE</th>
                  <th className="p-1 border border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100">DM</th>
                </tr>
              </thead>
              <tbody>
                {(currentSection as any)?.sensors ? (
                  <tr>
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-xs text-gray-900 dark:text-gray-100">{(currentSection as any).sensors.name || 'Standard'}</td>
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-center text-xs text-gray-900 dark:text-gray-100">
                      {(currentSection as any).sensors.dm > 0 ? `+${(currentSection as any).sensors.dm}` : (currentSection as any).sensors.dm || "—"}
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-xs text-gray-400 dark:text-gray-500">
                      No sensors
                    </td>
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-center text-xs text-gray-400 dark:text-gray-500">
                      —
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bottom Section - Weapons and Optional Systems spanning all columns */}
      <div className="mt-6">
        {/* Weapons - Full Width */}
        <div className="mb-6">
          <div className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
            WEAPONS
          </div>
          <table className="w-full border border-blue-200 dark:border-blue-700 rounded-b-md text-xs">
            <thead>
              <tr className="bg-blue-50 dark:bg-blue-900/30">
                <th className="p-1 border border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100">MOUNT</th>
                <th className="p-1 border border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100">WEAPON</th>
                <th className="p-1 border border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100">TL</th>
                <th className="p-1 border border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100">RANGE</th>
                <th className="p-1 border border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100">DAMAGE</th>
                <th className="p-1 border border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100">AMMUNITION</th>
                <th className="p-1 border border-blue-200 dark:border-blue-700 text-blue-900 dark:text-blue-100">TRAITS</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                if (!sectionWeapons || sectionWeapons.length === 0) {
                  // Show empty rows if no weapons
                  return [...Array(3)].map((_, i) => (
                    <tr key={i}>
                      <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                      <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                      <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                      <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                      <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                      <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                      <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                    </tr>
                  ));
                }

                // Group mounts by their type and weapons
                const mountGroups = sectionWeapons.reduce(
                  (groups, mount) => {
                    if (mount.weapons.length === 0) return groups;

                    // Create a unique key for grouping mounts by type and weapon properties
                    const mountKey = mount.weapons
                      .map((weapon) => {
                        return `${weapon.name}-${weapon.tl}-${weapon.damage}-${weapon.range}-${weapon.tons}-${weapon.cost}-${weapon.power}-${weapon.traits ? [...weapon.traits].sort().join(",") : ""}-${weapon.ammo?.name || "none"}-${weapon.ammo?.tl || "none"}-${weapon.ammo?.traits ? [...weapon.ammo.traits].sort().join(",") : "none"}-${weapon.ammo?.damage || "none"}`;
                      })
                      .sort()
                      .join("|");

                    const fullKey = `${mount.type}${mount.turretType ? `:${mount.turretType}` : ""}:${mountKey}`;
                    if (!groups[fullKey]) groups[fullKey] = [];
                    groups[fullKey].push(mount);
                    return groups;
                  },
                  {} as Record<string, typeof sectionWeapons>,
                );

                // Create mount display name function
                const getMountTypeDisplay = (type: string) => {
                  const typeMap: Record<string, string> = {
                    turret: "Turret",
                    barbette: "Barbette",
                    smallBay: "Small Bay",
                    mediumBay: "Medium Bay",
                    largeBay: "Large Bay",
                    spinalMount: "Spinal Mount",
                    pointDefense: "Point Defense",
                    fixed: "Fixed",
                  };
                  return typeMap[type] || type;
                };

                // Generate rows for each mount group
                const allRows: JSX.Element[] = [];

                Object.entries(mountGroups).forEach(
                  ([mountKey, mounts], groupIndex) => {
                    const firstMount = mounts[0];
                    const mountType = getMountTypeDisplay(firstMount.type);

                    // Get weapon names for display
                    const weaponNames = firstMount.weapons
                      .map((w) => w.name)
                      .slice()
                      .sort();
                    let mountDisplayName = `${mountType} (${weaponNames.join(", ")})`;

                    // Add turret type to display name if it's a turret
                    if (firstMount.type === "turret" && firstMount.turretType) {
                      const turretTypeDisplay =
                        firstMount.turretType.charAt(0).toUpperCase() +
                        firstMount.turretType.slice(1);
                      mountDisplayName = `${turretTypeDisplay} ${mountType} (${weaponNames.join(", ")})`;
                    }

                    // Show count if multiple identical mounts
                    const displayMountName =
                      mounts.length > 1
                        ? `${mountDisplayName} (×${mounts.length})`
                        : mountDisplayName;

                    // Group weapons within this mount group
                    const weaponGroups = (firstMount.weapons || []).reduce(
                      (groups, weapon) => {
                        // Create a unique key for grouping weapons by their properties
                        const weaponKey = `${weapon.name}-${weapon.tl}-${weapon.damage}-${weapon.range}-${weapon.tons}-${weapon.cost}-${weapon.power}-${weapon.traits ? [...weapon.traits].sort().join(",") : ""}-${weapon.ammo?.name || "none"}-${weapon.ammo?.tl || "none"}-${weapon.ammo?.traits ? [...weapon.ammo.traits].sort().join(",") : "none"}-${weapon.ammo?.damage || "none"}`;

                        if (!groups[weaponKey]) groups[weaponKey] = [];
                        groups[weaponKey].push(weapon);
                        return groups;
                      },
                      {} as Record<string, typeof firstMount.weapons>,
                    );

                    // Create rows for each weapon group
                    Object.entries(weaponGroups).forEach(
                      ([weaponKey, weapons], weaponGroupIndex) => {
                        const baseWeapon = weapons[0];
                        const isFirstWeapon = weaponGroupIndex === 0;

                        // Calculate total count across all mounts in this group
                        const totalWeaponCount = weapons.length * mounts.length;

                        // Show weapon count if multiple identical weapons
                        const displayWeaponName =
                          weapons.length > 1
                            ? `${baseWeapon.name} (×${weapons.length})`
                            : baseWeapon.name;

                        // Check if this specific weapon type has multiple weapons in a single mount
                        const sameTypeWeapons = firstMount.weapons.filter(
                          (w) => w.name === baseWeapon.name,
                        );
                        let combinedFireDamage = null;
                        if (
                          sameTypeWeapons.length > 1 &&
                          (firstMount.type === "turret" ||
                            firstMount.type === "fixed")
                        ) {
                          const baseWeaponForCalc = sameTypeWeapons[0];
                          const baseDamage = baseWeaponForCalc.damage;
                          const additionalWeapons = sameTypeWeapons.length - 1;
                          const damageMatch = baseDamage.match(/(\d+)D/);
                          if (damageMatch) {
                            const diceCount = parseInt(damageMatch[1]);
                            const bonus = additionalWeapons * diceCount;
                            const damageMultiple =
                              baseWeaponForCalc.damageMultiple || 1;
                            if (damageMultiple > 1) {
                              combinedFireDamage = `(${baseDamage}+${bonus}) × ${damageMultiple} (${sameTypeWeapons.length} weapons)`;
                            } else {
                              combinedFireDamage = `${baseDamage}+${bonus} (${sameTypeWeapons.length} weapons)`;
                            }
                          } else {
                            const damageMultiple =
                              baseWeaponForCalc.damageMultiple || 1;
                            if (damageMultiple > 1) {
                              combinedFireDamage = `${baseDamage} × ${damageMultiple} (${sameTypeWeapons.length} weapons)`;
                            } else {
                              combinedFireDamage = `${baseDamage} (${sameTypeWeapons.length} weapons)`;
                            }
                          }
                        }

                        // If no combined fire found in first mount, check other mounts in the group
                        if (
                          !combinedFireDamage &&
                          (firstMount.type === "turret" ||
                            firstMount.type === "fixed")
                        ) {
                          for (const mount of mounts) {
                            const mountSameTypeWeapons = mount.weapons.filter(
                              (w) => w.name === baseWeapon.name,
                            );
                            if (mountSameTypeWeapons.length > 1) {
                              const baseWeaponForCalc = mountSameTypeWeapons[0];
                              const baseDamage = baseWeaponForCalc.damage;
                              const additionalWeapons =
                                mountSameTypeWeapons.length - 1;
                              const damageMatch = baseDamage.match(/(\d+)D/);
                              if (damageMatch) {
                                const diceCount = parseInt(damageMatch[1]);
                                const bonus = additionalWeapons * diceCount;
                                const damageMultiple =
                                  baseWeaponForCalc.damageMultiple || 1;
                                if (damageMultiple > 1) {
                                  combinedFireDamage = `(${baseDamage}+${bonus}) × ${damageMultiple} (${mountSameTypeWeapons.length} weapons)`;
                                } else {
                                  combinedFireDamage = `${baseDamage}+${bonus} (${mountSameTypeWeapons.length} weapons)`;
                                }
                              } else {
                                const damageMultiple =
                                  baseWeaponForCalc.damageMultiple || 1;
                                if (damageMultiple > 1) {
                                  combinedFireDamage = `${baseDamage} × ${damageMultiple} (${mountSameTypeWeapons.length} weapons)`;
                                } else {
                                  combinedFireDamage = `${baseDamage} (${mountSameTypeWeapons.length} weapons)`;
                                }
                              }
                              break; // Found one, that's enough
                            }
                          }
                        }

                        allRows.push(
                          <tr
                            key={`${groupIndex}-${baseWeapon.name}-${weaponGroupIndex}`}
                          >
                            <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-xs text-gray-900 dark:text-gray-100">
                              {isFirstWeapon ? displayMountName : ""}
                            </td>
                            <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-xs text-gray-900 dark:text-gray-100">
                              {displayWeaponName}
                            </td>
                            <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-xs text-center text-gray-900 dark:text-gray-100">
                              {baseWeapon.tl || "—"}
                            </td>
                            <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-xs text-center text-gray-900 dark:text-gray-100">
                              {baseWeapon.range || "—"}
                            </td>
                            <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-xs text-center text-gray-900 dark:text-gray-100">
                              <div>
                                <div>{baseWeapon.damage}</div>
                                {combinedFireDamage && (
                                  <div className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
                                    Combined: {combinedFireDamage}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-xs text-center text-gray-900 dark:text-gray-100">
                              {baseWeapon.ammo ? (
                                <div>
                                  <div>
                                    {baseWeapon.ammo.name || baseWeapon.ammo.type} ×{baseWeapon.ammo.capacity || 0}
                                  </div>
                                  {totalWeaponCount > 1 && (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      ×{totalWeaponCount} mounts
                                    </div>
                                  )}
                                  {baseWeapon.extraReloads &&
                                    baseWeapon.extraReloads > 0 && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400">
                                        +{baseWeapon.extraReloads} reloads
                                      </div>
                                    )}
                                </div>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="border border-blue-200 dark:border-blue-700 p-1 h-6 text-xs text-center text-gray-900 dark:text-gray-100">
                              {baseWeapon.traits &&
                              Array.isArray(baseWeapon.traits) &&
                              baseWeapon.traits.length > 0
                                ? baseWeapon.traits.join(", ")
                                : "—"}
                            </td>
                          </tr>,
                        );
                      },
                    );
                  },
                );

                return allRows;
              })()}
              {/* Add additional empty rows to maintain minimum table height if needed */}
              {(() => {
                const totalWeaponRows = sectionWeapons
                  ? sectionWeapons.reduce((total, mount) => {
                      return total + mount.weapons.length;
                    }, 0)
                  : 0;

                const neededRows = Math.max(0, 3 - totalWeaponRows);
                return [...Array(neededRows)].map((_, i) => (
                  <tr key={`empty-${i}`}>
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                    <td className="border border-blue-200 dark:border-blue-700 p-1 h-6" />
                  </tr>
                ));
              })()}
            </tbody>
          </table>
        </div>

        {/* Optional Systems - Full Width */}
        <div className="mb-6">
          <div className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
            SYSTEMS
          </div>
          <div className="border border-t-0 border-blue-200 dark:border-blue-700 rounded-b-md p-3 text-xs">
            {(() => {
              if (sectionOptionalSystems.length === 0) {
                return (
                  <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                    No optional systems installed
                  </div>
                );
              }

              // Helper function to get system details
              const getSystemDetails = (system: any) => {
                // Structural system details
                if (
                  system.type === "structure" &&
                  system.subtype === "armouredBulkhead"
                ) {
                  if (
                    system.protectedSystems &&
                    Array.isArray(system.protectedSystems)
                  ) {
                    const protectedSystems = system.protectedSystems;
                    const formattedNames = protectedSystems.map(
                      (systemName: string) => {
                        const nameMap: { [key: string]: string } = {
                          staterooms: "Staterooms",
                          reactionDrive: "Reaction Drive",
                          maneuverDrive: "Maneuver Drive",
                          jumpDrive: "Jump Drive",
                          powerPlant: "Power Plant",
                          bridge: "Bridge",
                          sensors: "Sensors",
                          weapons: "Weapons",
                          cargo: "Cargo",
                          fuel: "Fuel",
                          passengers: "Passengers",
                          crew: "Crew",
                        };
                        return nameMap[systemName] || systemName;
                      },
                    );
                    return formattedNames.join(", ");
                  }
                  return null;
                }

                // Internal system details
                if (system.type === "internal") {
                  switch (system.subtype) {
                    case "internalMedicalBay":
                      return "3 patients";
                    case "internalDockingSpace":
                      if (typeof system.tonnage === "number") {
                        const maxVesselSize = Math.floor((system.tonnage * 10) / 11);
                        return `${maxVesselSize} tons`;
                      }
                      return null;
                    case "internalConcealedCompartment":
                      if (typeof system.tonnage === "number") {
                        return `${system.tonnage} tons`;
                      }
                      return null;
                    case "internalConstructionDeck":
                      if (typeof system.tonnage === "number") {
                        const buildCapacity = Math.floor((system.tonnage * 5) / 10);
                        return `${buildCapacity} tons`;
                      }
                      return null;
                    case "internalFullHangar":
                      if (typeof system.tonnage === "number") {
                        const craftCapacity = Math.floor(system.tonnage / 2);
                        return `${craftCapacity} tons`;
                      }
                      return null;
                    case "internalGravScreen":
                      if (typeof system.tonnage === "number") {
                        return `${section.size} ton hull`;
                      }
                      return null;
                    default:
                      return null;
                  }
                }

                // Drone system details
                if (system.type === "drone") {
                  switch (system.subtype) {
                    case "mining":
                    case "probe":
                    case "advancedProbe":
                    case "repair":
                      return "1";
                    default:
                      return null;
                  }
                }

                // Power system details
                if (system.type === "power") {
                  switch (system.subtype) {
                    case "auxiliary":
                    case "emergency":
                      return null; // No details shown
                    case "battery":
                      const storageCapacity = system.power || 0;
                      return `${storageCapacity.toFixed(1)} Power`;
                    case "solarPanels":
                      const powerPerUnit =
                        system.properties?.powerPerUnit?.[system.techLevel] || 1;
                      const deploymentPower = system.tonnage * powerPerUnit;
                      return `${deploymentPower.toFixed(1)} Power`;
                    default:
                      return null;
                  }
                }

                // Accommodation system details
                if (system.type === "accommodation") {
                  switch (system.subtype) {
                    case "accelerationBench":
                    case "accelerationSeat":
                      if (system.properties?.seats) {
                        const seats = Number(system.properties.seats) || 0;
                        const quantity = Number(system.properties.quantity) || 1;
                        const totalSeats = seats * quantity;
                        return `${totalSeats} passengers`;
                      }
                      return null;
                    case "barracks":
                      if (system.properties?.quantity) {
                        const people = Number(system.properties.quantity) || 1;
                        const designation =
                          system.properties.designation || "passenger";
                        const designationText =
                          designation === "crew" ? "Crew" : "Basic Passage";
                        return `${people} ${designationText}`;
                      }
                      return null;
                    default:
                      return null;
                  }
                }

                // Common area details
                if (system.type === "commonArea") {
                  switch (system.subtype) {
                    case "advancedEntertainment":
                      return `TL${system.techLevel}`;
                    case "gourmetKitchen":
                      if (system.properties?.diners) {
                        return `${system.properties.diners} diners`;
                      }
                      return null;
                    case "hotTub":
                      if (system.properties?.travellers) {
                        return `${system.properties.travellers} travellers`;
                      }
                      return null;
                    case "swimmingPool":
                    case "endlessPool":
                      const poolType = system.properties?.poolType || "";
                      const travellers = system.properties?.travellers
                        ? `${system.properties.travellers} travellers`
                        : "";
                      return `${poolType} ${travellers}`.trim();
                    case "zeroGRoom":
                      return `${system.tonnage} tons`;
                    default:
                      return null;
                  }
                }

                return null;
              };

              // Group systems by type
              const groupedSystems = sectionOptionalSystems.reduce(
                (groups: Record<string, any[]>, system) => {
                  const type = system.type || "other";
                  if (!groups[type]) {
                    groups[type] = [];
                  }
                  groups[type].push(system);
                  return groups;
                },
                {},
              );

              // Define display names for system types
              const getTypeDisplayName = (type: string) => {
                switch (type) {
                  case "structure":
                    return "STRUCTURAL";
                  case "power":
                    return "POWER SYSTEMS";
                  case "drives":
                    return "DRIVE SYSTEMS";
                  case "fuel":
                    return "FUEL SYSTEMS";
                  case "accommodations":
                    return "ACCOMMODATIONS";
                  case "bridge":
                    return "BRIDGE SYSTEMS";
                  case "cargo":
                    return "CARGO SYSTEMS";
                  case "internal":
                    return "INTERNAL SYSTEMS";
                  case "external":
                    return "EXTERNAL SYSTEMS";
                  case "sensor":
                    return "SENSOR SYSTEMS";
                  case "software":
                    return "SOFTWARE SYSTEMS";
                  case "weapon":
                    return "WEAPON SYSTEMS";
                  case "defensive":
                    return "DEFENSIVE SYSTEMS";
                  case "commonArea":
                    return "COMMON AREAS";
                  case "drone":
                    return "DRONE";
                  default:
                    return type.toUpperCase();
                }
              };

              // Define colors for system types
              const getTypeColor = (type: string) => {
                switch (type) {
                  case "structure":
                    return "bg-gray-200 text-gray-900";
                  case "power":
                    return "bg-yellow-200 text-yellow-900";
                  case "drives":
                    return "bg-blue-200 text-blue-900";
                  case "fuel":
                    return "bg-orange-200 text-orange-900";
                  case "accommodations":
                    return "bg-purple-200 text-purple-900";
                  case "bridge":
                    return "bg-indigo-200 text-indigo-900";
                  case "cargo":
                    return "bg-green-200 text-green-900";
                  case "internal":
                    return "bg-pink-200 text-pink-900";
                  case "external":
                    return "bg-teal-200 text-teal-900";
                  case "sensor":
                    return "bg-cyan-200 text-cyan-900";
                  case "software":
                    return "bg-violet-200 text-violet-900";
                  case "weapon":
                    return "bg-red-200 text-red-900";
                  case "defensive":
                    return "bg-amber-200 text-amber-900";
                  case "commonArea":
                    return "bg-gray-200 text-gray-900";
                  case "drone":
                    return "bg-gray-200 text-gray-900";
                  default:
                    return "bg-gray-200 text-gray-900";
                }
              };

              const groupedEntries = Object.entries(groupedSystems) as [string, any[]][];

              // Function to create a system box
              const createSystemBox = (type: string, systems: any[]) => {
                return (
                  <div
                    key={type}
                    className="text-xs border border-gray-300 dark:border-gray-600 rounded-md p-2 bg-white dark:bg-gray-700 flex-shrink-0 max-w-2xl min-w-fit"
                  >
                    <div
                      className={`${getTypeColor(type)} font-bold px-2 py-1 rounded text-xs tracking-wide mb-2`}
                    >
                      {getTypeDisplayName(type)}
                    </div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-300 dark:border-gray-600">
                          <th className="text-left py-1 font-medium text-gray-700 dark:text-gray-300 text-xs border-r border-gray-300 dark:border-gray-600 pr-2">
                            System Name
                          </th>
                          <th className="text-left py-1 font-medium text-gray-700 dark:text-gray-300 text-xs pl-2">
                            Details
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {systems.map((system: any, index: number) => {
                          const details = getSystemDetails(system);
                          return (
                            <tr
                              key={index}
                              className="border-b border-gray-200 dark:border-gray-600 text-xs"
                            >
                              <td className="py-1 font-medium text-gray-800 dark:text-gray-200 text-xs border-r border-gray-300 dark:border-gray-600 pr-2">
                                {system.name}
                              </td>
                              <td className="py-1 text-gray-600 dark:text-gray-300 text-xs pl-2">
                                {details || "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              };

              // Arrange system boxes in a flex layout
              const systemBoxes = groupedEntries.map(([type, systems]) =>
                createSystemBox(type, systems),
              );

              return (
                <div className="flex flex-wrap gap-4 justify-start">
                  {systemBoxes}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Staterooms and Cargo - Half page each */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Staterooms and Passengers - Half page */}
          <div>
            <div className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              STATEROOMS AND PASSENGERS
            </div>
            <div className="border border-t-0 border-blue-200 dark:border-blue-700 rounded-b-md p-3 text-xs">
              <div className="grid grid-cols-2 gap-4">
                {/* Staterooms */}
                <div>
                  <div className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                    Staterooms
                  </div>
                  <div className="space-y-1 text-gray-900 dark:text-gray-100">
                    <div className="flex justify-between">
                      <span>Standard Staterooms:</span>
                      <span className="font-semibold">
                        {currentSection?.stateroomConfig?.singleStaterooms || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Double Staterooms:</span>
                      <span className="font-semibold">
                        {currentSection?.stateroomConfig?.doubleStaterooms || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Low Berths:</span>
                      <span className="font-semibold">
                        {currentSection?.stateroomConfig?.lowBerths || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Emergency Low Berths:</span>
                      <span className="font-semibold">
                        {currentSection?.stateroomConfig?.emergencyLowBerths || 0}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Common Area:</span>
                      <span className="font-semibold">
                        {currentSection?.stateroomConfig?.commonArea || 0} tons
                      </span>
                    </div>
                  </div>
                </div>

                {/* Passengers */}
                <div>
                  <div className="font-semibold text-gray-800 dark:text-gray-200 mb-2">
                    Passengers
                  </div>
                  <div className="space-y-1 text-gray-900 dark:text-gray-100">
                    <div className="flex justify-between">
                      <span>High Passengers:</span>
                      <span className="font-semibold">
                        0
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Middle Passengers:</span>
                      <span className="font-semibold">
                        0
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Basic Passengers:</span>
                      <span className="font-semibold">
                        0
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Low Passengers:</span>
                      <span className="font-semibold">
                        0
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Passengers:</span>
                      <span className="font-semibold">
                        0
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Cargo Hold Content - Half page */}
          <div>
            <div className="bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100 font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
              CARGO HOLD CONTENT
            </div>
            <div className="border border-t-0 border-blue-200 dark:border-blue-700 rounded-b-md p-3 text-xs">
              <div className="text-gray-500 dark:text-gray-400 italic">No cargo assigned</div>
            </div>
          </div>
        </div>
      </div>

      {/* Critical Hits Section */}
      <div className="mt-6 critical-hits-section">
        <div className="bg-orange-400 text-white font-bold px-3 py-1 rounded-t-md text-xs tracking-wide">
          CRITICAL HITS
        </div>
        <div className="border border-t-0 border-orange-400 rounded-b-md p-3 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
            {(() => {
              const labels = [
                "ARMOUR",
                "BRIDGE", 
                "CARGO",
                "CREW",
                "FUEL",
                "HULL",
                "J-DRIVE",
                "M-DRIVE",
                "POWER PLANT",
                "SENSORS",
                "WEAPONS",
              ];
              
              return labels.map((label) => (
                <div key={label} className="flex items-center gap-3 min-w-0">
                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 whitespace-nowrap min-w-0 flex-shrink-0">
                    {label}
                  </span>
                  <div className="flex gap-1 flex-shrink-0">
                    {[...Array(6)].map((_, i) => (
                      <span
                        key={i}
                        className="w-4 h-4 border border-gray-400 dark:border-gray-500 rounded-full inline-block"
                      />
                    ))}
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* Deckplans Section */}
      <div className="mt-6 deckplans-section">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Deckplans</h3>
            <button className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
              Add Deckplan Upload
            </button>
          </div>
          <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-3">Upload New Deckplans</h4>
        </div>

        <div className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
          <div className="mb-4">
            <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Deckplan 1</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* File Upload Area */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Upload Image
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
                  <div className="space-y-2">
                    <div className="text-gray-400 dark:text-gray-500">
                      <svg className="mx-auto h-8 w-8" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <span className="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 cursor-pointer">
                        Click to upload
                      </span>{" "}
                      or drag and drop
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      PNG, JPG, GIF up to 10MB
                    </div>
                  </div>
                </div>
              </div>

              {/* Input Fields */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Deckplan Name *
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Main Deck, Bridge Deck"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description
                  </label>
                  <textarea
                    placeholder="Optional description of this deckplan"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none dark:bg-gray-800 dark:text-gray-100"
                  />
                </div>
                <div className="flex justify-end">
                  <button className="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500">
                    Save Deckplan
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};